import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "material-calendar-range" }
const _hoisted_2 = { class: "range-header" }
const _hoisted_3 = { class: "month-label" }
const _hoisted_4 = { class: "calendar-grid" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "day-number" }
const _hoisted_7 = {
  key: 0,
  class: "data-dot"
}
const _hoisted_8 = { class: "range-footer" }
const _hoisted_9 = {
  key: 0,
  class: "selected-range"
}
const _hoisted_10 = {
  key: 1,
  class: "range-instruction"
}
const _hoisted_11 = { class: "range-actions" }
const _hoisted_12 = ["disabled"]

import { ref, computed, watch} from 'vue';

interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MaterialCalendarRange',
  props: {
    activityDates: {},
    selectedStartDate: {},
    selectedEndDate: {}
  },
  emits: ['close', 'range-select'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

function parseYYYYMMDD(dateStr?: string): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const today = new Date();

const initialDate =
  parseYYYYMMDD(props.selectedEndDate) ||
  parseYYYYMMDD(props.selectedStartDate) ||
  today;

const currentMonth = ref(initialDate.getMonth());
const currentYear = ref(initialDate.getFullYear());

const weekDays = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

const currentMonthName = computed(() => {
  return new Date(currentYear.value, currentMonth.value).toLocaleString('it-IT', { month: 'long' });
});

const calendarDays = computed<CalendarDay[]>(() => {
  const days: CalendarDay[] = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
  
  // Adjust logic for Monday start (0=Sun -> 6, 1=Mon -> 0)
  let startDayOfWeek = firstDay.getDay() - 1; 
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  // Previous month padding
  const prevMonthLastDay = new Date(currentYear.value, currentMonth.value, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value - 1, prevMonthLastDay - i),
       day: prevMonthLastDay - i,
       currentMonth: false
     });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      date: new Date(currentYear.value, currentMonth.value, i),
      day: i,
      currentMonth: true
    });
  }

  // Next month padding
  const remainingCells = 42 - days.length; // 6 rows * 7
  for (let i = 1; i <= remainingCells; i++) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value + 1, i),
       day: i,
       currentMonth: false
     });
  }

  return days;
});

// Range selection logic
const selectedStartDate = ref<string>(props.selectedStartDate || '');
const selectedEndDate = ref<string>(props.selectedEndDate || '');

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function isToday(date: Date) {
  return formatDate(date) === formatDate(new Date());
}

function hasData(date: Date) {
  if (!props.activityDates) return false;
  return props.activityDates.includes(formatDate(date));
}

function isStartDate(date: Date) {
  return selectedStartDate.value === formatDate(date);
}

function isEndDate(date: Date) {
  return selectedEndDate.value === formatDate(date);
}

function isInRange(date: Date) {
  if (!selectedStartDate.value || !selectedEndDate.value) return false;
  
  const dateStr = formatDate(date);
  const start = selectedStartDate.value;
  const end = selectedEndDate.value;
  
  // Ordina start e end (potrebbero essere selezionati in ordine inverso)
  const [sortedStart, sortedEnd] = [start, end].sort();
  
  return dateStr >= sortedStart && dateStr <= sortedEnd;
}

// function selectDate(date: Date) {
//   const dateStr = formatDate(date);
  
//   if (!selectedStartDate.value) {
//     // Prima selezione: imposta start date
//     selectedStartDate.value = dateStr;
//     selectedEndDate.value = dateStr;
//   } else if (!selectedEndDate.value) {
//     // Seconda selezione: imposta end date
//     selectedEndDate.value = dateStr;
    
//     // Se end date < start date, scambia
//     if (selectedEndDate.value < selectedStartDate.value) {
//       [selectedStartDate.value, selectedEndDate.value] = [selectedEndDate.value, selectedStartDate.value];
//     }
//   } else {
//     // Reset e ricomincia
//     selectedStartDate.value = dateStr;
//     selectedEndDate.value = '';
//   }
// }
function selectDate(date: Date) {
  const dateStr = formatDate(date);

  if (!selectedStartDate.value) {
    selectedStartDate.value = dateStr;
    selectedEndDate.value = '';
    return;
  }

  if (!selectedEndDate.value) {
    if (dateStr < selectedStartDate.value) {
      selectedEndDate.value = selectedStartDate.value;
      selectedStartDate.value = dateStr;
    } else {
      selectedEndDate.value = dateStr;
    }
    return;
  }

  selectedStartDate.value = dateStr;
  selectedEndDate.value = '';
}

function applyRange() {
  if (!selectedStartDate.value || !selectedEndDate.value) return;
  
  emit('range-select', {
    start: selectedStartDate.value,
    end: selectedEndDate.value
  });
  emit('close');
}

function clearRange() {
  selectedStartDate.value = '';
  selectedEndDate.value = '';
}

watch(
  () => props.selectedStartDate,
  (val) => {
    selectedStartDate.value = val || '';
  },
  { immediate: true }
);

watch(
  () => props.selectedEndDate,
  (val) => {
    selectedEndDate.value = val || '';
  },
  { immediate: true }
);

watch(
  () => [props.selectedStartDate, props.selectedEndDate],
  ([start, end]) => {
    const d = parseYYYYMMDD(end || start || '');
    if (d) {
      currentMonth.value = d.getMonth();
      currentYear.value = d.getFullYear();
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        class: "nav-btn",
        onClick: prevMonth
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z",
            fill: "currentColor"
          })
        ], -1)
      ]))]),
      _createElementVNode("span", _hoisted_3, _toDisplayString(currentMonthName.value) + " " + _toDisplayString(currentYear.value), 1),
      _createElementVNode("button", {
        class: "nav-btn",
        onClick: nextMonth
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("svg", {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z",
            fill: "currentColor"
          })
        ], -1)
      ]))])
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(weekDays, (day) => {
        return _createElementVNode("div", {
          key: day,
          class: "weekday-label"
        }, _toDisplayString(day), 1)
      }), 64)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (date, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass(["day-cell", {
          'current-month': date.currentMonth,
          'selected-start': isStartDate(date.date),
          'selected-end': isEndDate(date.date),
          'in-range': isInRange(date.date),
          'has-data': hasData(date.date),
          'today': isToday(date.date)
        }]),
          onClick: ($event: any) => (selectDate(date.date))
        }, [
          _createElementVNode("span", _hoisted_6, _toDisplayString(date.day), 1),
          (hasData(date.date))
            ? (_openBlock(), _createElementBlock("div", _hoisted_7))
            : _createCommentVNode("", true)
        ], 10, _hoisted_5))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_8, [
      (selectedStartDate.value && selectedEndDate.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, " Selezionato: " + _toDisplayString(formatDateDisplay(selectedStartDate.value)) + " - " + _toDisplayString(formatDateDisplay(selectedEndDate.value)), 1))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, " Clicca per selezionare data di inizio e fine ")),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          class: "btn-apply",
          onClick: applyRange,
          disabled: !selectedStartDate.value || !selectedEndDate.value
        }, " Applica ", 8, _hoisted_12),
        _createElementVNode("button", {
          class: "btn-clear",
          onClick: clearRange
        }, " Cancella ")
      ])
    ])
  ]))
}
}

})